import { useEffect, useRef } from 'react';
import { EventSource, type FetchLike } from 'eventsource';
import { connectorAuth } from 'service/connectorRuntime';
import { fetchWithRenewal } from 'service/amsFetch';

const SSE_RECONNECT_BASE_MS = 1_000;
export const SSE_RECONNECT_MAX_MS = 30_000;
const SSE_RECONNECT_JITTER_MS = 1_000;

/**
 * Fetch hook for SSE streams: same auth seams as the REST connectors
 * (connectorAuth resolves the AMS access token, else the legacy Cognito
 * id_token), resolved fresh on every (re)connect. fetchWithRenewal is
 * load-bearing, not optional: after laptop sleep the in-memory AMS token is
 * stale, and a bare fetch would 401 -> CLOSED -> reconnect with the same
 * stale token until the session's renewal timer fires; fetchWithRenewal
 * renews-and-retries within the same connect. Legacy sessions pass through.
 */
const sseFetch: FetchLike = (url, init) => {
  const request = new Request(url, init);
  const token = connectorAuth();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return fetchWithRenewal(request);
};

export type UseSseStreamOptions = {
  /** Stream URL. null disables the stream: nothing connects and any open stream is closed. */
  url: string | null;
  /** Raw `data:` payload of every frame. Parsing belongs to the consumer. */
  onMessage: (data: string) => void;
  /** Identity behind the stream (user, tenant). A change closes the stream and reconnects with a fresh token. */
  reconnectKey?: string;
  /** Close the stream once the tab has been hidden this long; omit to keep hidden tabs connected. */
  hiddenGraceMs?: number;
};

/**
 * Owns one authenticated Server-Sent Events connection for the lifetime of the
 * caller. The fetch-based `eventsource` package mirrors the native client's
 * readyState split, plus the `fetch` hook for the bearer:
 * - transient drop -> readyState CONNECTING: the package retries on its own.
 * - fatal close -> readyState CLOSED (e.g. a non-200 connect such as 401):
 *   recreate with capped exponential backoff + jitter; any received frame
 *   resets the backoff.
 * - hidden tab (when hiddenGraceMs is set) -> close after the grace period,
 *   reopen on the next visibilitychange to visible.
 * - unmount / url null / reconnectKey change -> close and cancel every timer.
 */
export function useSseStream({ url, onMessage, reconnectKey, hiddenGraceMs }: UseSseStreamOptions): void {
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (url === null) {
      return undefined;
    }

    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let disposed = false;

    const connect = () => {
      if (disposed) { return; }
      es = new EventSource(url, { fetch: sseFetch });

      es.onmessage = (event: MessageEvent<string>) => {
        attempt = 0;
        onMessageRef.current(event.data);
      };

      es.onerror = (event) => {
        if (!es || es.readyState !== EventSource.CLOSED) {
          return; // CONNECTING: the package's internal retry is already underway.
        }
        es.close();
        es = null;
        const delay =
          Math.min(SSE_RECONNECT_BASE_MS * 2 ** attempt, SSE_RECONNECT_MAX_MS) + Math.random() * SSE_RECONNECT_JITTER_MS;
        // `code` is the HTTP status of a failed connect (401 vs network at a glance).
        console.warn('[sse] %s closed (code %s); reconnecting in %dms (attempt %d)', url, event.code ?? 'n/a', Math.round(delay), attempt + 1);
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    const disconnect = () => {
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      es?.close();
      es = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (hiddenTimer === null) {
          hiddenTimer = setTimeout(() => {
            hiddenTimer = null;
            disconnect();
          }, hiddenGraceMs);
        }
        return;
      }
      if (hiddenTimer !== null) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
      if (es === null && retryTimer === null) {
        attempt = 0;
        connect();
      }
    };

    if (hiddenGraceMs !== undefined) {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    connect();

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (hiddenTimer !== null) { clearTimeout(hiddenTimer); }
      disconnect();
    };
  }, [url, reconnectKey, hiddenGraceMs]);
}
